import React from "react";

import { Button } from "react-bootstrap";

import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "src/routes";

import HomepageBannerImg from "@assets/Images/HomepageBannerImg.jpg";
import MoreInfoACBannerMobileImg from "@assets/Images/MoreInfoACBannerMobileImg.jpg";
import MoreInfoAMPBannerMobileImg from "@assets/Images/MoreInfoAMPBannerMobileImg.jpg";
import MoreInfoArtistBannerMobileImg from "@assets/Images/MoreInfoArtistBannerMobileImg.jpg";
import FAQAccordionGroup from "@components/component-library/Atoms/FAQAccordionGroup/FAQAccordionGroup";
import BannerThreeToEightCols from "@components/component-library/Molecules/BannerThreeToEightCols/BannerThreeToEightCols";
import BannerTwoToTwoCols from "@components/component-library/Molecules/BannerTwoToTwoCols/BannerTwoToTwoCols";
import ScrollTracker from "@components/component-library/Molecules/ScrollTracker/ScrollTracker";
import PublicPagesWrapper from "@components/component-library/Wrappers/PublicPagesWrapper";
import { useAuthenticationContext } from "@context/authentication";
import { FAQs } from "@util/FAQs";

import AcknowledgementOfCountryModal from "./AcknowledgementOfCountryModal";

import styles from "./Home.module.scss";

export const Home = () => {
  const { auth } = useAuthenticationContext();
  const navigate = useNavigate();

  return (
    <PublicPagesWrapper>
      <AcknowledgementOfCountryModal />
      <h1 className={classNames(styles["hompage_heading_1"], "col-md-8")}>
        Welcome to the <span className={styles["hompage_heading_1_rrs"]}>resale royalty scheme</span> for visual artists
      </h1>
      <Button
        variant="primary"
        role="link"
        size="lg"
        onClick={() => {
          navigate(auth?.signInDetails?.loginId ? ROUTES.Dashboard.path : ROUTES.ExternalRegister.path);
        }}
      >
        {auth?.signInDetails?.loginId ? "Dashboard" : "Register now"}
      </Button>
      <div className={styles["card-image"]}>
        <div className={styles["image-container"]}>
          <img src={HomepageBannerImg} alt="Banner image" className={styles["banner-image"]} />
          <div className={styles["image-text"]}>
            The resale royalty scheme is an Australian Government initiative, managed by Copyright Agency. The scheme
            has generated over $15 million in royalties for artists and beneficiaries since 2010.{" "}
            <div className="pb-3 pb-md-5" />
            Find out how the scheme works, and how to report resales or to receive royalties.{" "}
          </div>
        </div>
        <caption className="align-self-end text-end monoGrey1 b5 medium py-0">image credit: Daria Zaseda</caption>
      </div>
      <div className={styles["about-wrapper"]}>
        <BannerThreeToEightCols
          heading="About the scheme"
          content={
            <div className="h3 mb-0">
              <p className="mb-4">
                Under the scheme, visual artists and other right holders are entitled to 5% of the sale price of
                eligible artworks resold commercially for $1,000 or more in Australia.
              </p>
              <p className="mb-4">
                From 31 March 2024, resale royalty entitlements expanded to include 17 countries that have reciprocal
                arrangements with Australia; and 9 additional countries were added from 31 March 2025.
              </p>
              <p className="mb-4">
                Since commencement in June 2010, the scheme has
                <ul>
                  <li className="mb-2">generated more than $15 million in royalties</li>
                  <li className="mb-2">for nearly 3,000 artists</li>
                  <li className="mb-2">for more than 33,000 resales</li>
                  <li className="mb-2">with most royalties between $50 and $500</li>
                </ul>
              </p>
            </div>
          }
        />
      </div>
      <ScrollTracker
        heading="What happens when an artwork is sold"
        steps={[
          {
            heading: "Resale is reported",
            subtext:
              "Art market professionals (such as auction houses and galleries) report the resale to Copyright Agency. We review the report and determine if the resale is eligible.",
          },
          {
            heading: "Artists are notified",
            subtext: "We notify artists, or their beneficiary, of the eligible resale of their work.",
          },
          {
            heading: "Royalties are invoiced and collected",
            subtext: "We issue an invoice to the art market professional and collect the royalty payment from them.",
          },
          {
            heading: "Royalty is paid",
            subtext: "We pay the royalty to the artist or their beneficiary. ",
          },
        ]}
      />
      <div className={styles["more-info-section-wrapper"]}>
        <div className="h2 mb-5 pb-3">Who needs to register</div>
        <div className={styles["more-info-cards-wrapper"]}>
          <div
            className={styles["more-info-card-image"]}
            role="link"
            onClick={() => navigate(ROUTES.MoreInfo.Artist.path)}
          >
            <div className={styles["image-container"]}>
              <img
                src={MoreInfoArtistBannerMobileImg}
                alt="Banner image"
                className={styles["more-info-banner-image"]}
              />
              <h4 className={styles["more-info-image-text"]}>Artist</h4>
            </div>
            <caption className="align-self-end text-end monoGrey1 b5 medium py-0">image credit: AlexD75</caption>
          </div>
          <div
            className={styles["more-info-card-image"]}
            role="link"
            onClick={() => navigate(ROUTES.MoreInfo.AMP.path)}
          >
            <div className={styles["image-container"]}>
              <img src={MoreInfoAMPBannerMobileImg} alt="Banner image" className={styles["more-info-banner-image"]} />
              <h4 className={styles["more-info-image-text"]}>Art market professionals</h4>
            </div>
            <caption className="align-self-end text-end monoGrey1 b5 medium py-0">
              image credit: Jacobs Stock Photography Ltd
            </caption>
          </div>
          <div className={styles["more-info-card-image"]} role="link" onClick={() => navigate(ROUTES.MoreInfo.AC.path)}>
            <div className={styles["image-container"]}>
              <img src={MoreInfoACBannerMobileImg} alt="Banner image" className={styles["more-info-banner-image"]} />
              <h4 className={styles["more-info-image-text"]}>Art centre</h4>
            </div>
            <caption className="align-self-end text-end monoGrey1 b5 medium py-0">image credit: Felix Cesare</caption>
          </div>
        </div>
      </div>
      <BannerTwoToTwoCols
        heading="Helpful resources"
        columnOneContent={{
          columnHeading: "Code of Practice",
          columnContent:
            "The purpose of this Code of Practice is to set out how Copyright Agency and art market professionals will deal with key aspects of the scheme. It’s intended to be read in conjunction with other guides and information about the scheme.",
          buttonLabel: "Read the Code",
          href: "/RESALE_ROYALTY_CODE_PRACTICE_2024.pdf",
        }}
        columnTwoContent={{
          columnHeading: "Comprehensive guide",
          columnContent:
            "This guide is primarily for art market professionals, who include art dealers, commercial galleries and auction houses. It’s also for artists and their beneficiaries, and people who buy and sell art.",
          buttonLabel: "Find out more",
          href: "/COMPREHENSIVE_GUIDE_TO_ARTISTS_RESALE_ROYALTY_SCHEME_2025.pdf",
        }}
      />
      <div className={styles["faq-wrapper"]}>
        <BannerThreeToEightCols
          heading="FAQs"
          content={[
            {
              ButtonLabel: "Artists",
              TabContent: (
                <div>
                  <FAQAccordionGroup qnas={FAQs.artist} />
                </div>
              ),
            },
            {
              ButtonLabel: "Art market professionals",
              TabContent: (
                <div>
                  <FAQAccordionGroup qnas={FAQs.amp} />
                </div>
              ),
            },
            {
              ButtonLabel: "Art centres",
              TabContent: (
                <div>
                  <FAQAccordionGroup qnas={FAQs.artCenter} />
                </div>
              ),
            },
            {
              ButtonLabel: "General",
              TabContent: (
                <div>
                  <FAQAccordionGroup qnas={FAQs.general} />
                </div>
              ),
            },
          ]}
        />
      </div>
    </PublicPagesWrapper>
  );
};
